import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  DeleteOutline,
} from '@mui/icons-material';

import { useArnoSelectorCatalog } from '../../hooks/reactQuerys/ArnoAdminQueries';
import { fieldLabelResolver } from './fieldPathLabel';
import {
  addTableColumn,
  conditionSummary,
  inlineConditionText,
  isInlineCondition,
  CARD_NODE_KINDS,
  moveAt,
  addTableRow,
  ANSWER_TYPE_LABEL,
  AnswerType,
  BRANCH_NODE_KINDS,
  hasMergedColumns,
  hasMergedRows,
  IBlockReference,
  IChoiceOption,
  IEditorNode,
  NodeKind,
  newNode,
  NODE_KIND_LABEL,
  removeAt,
  removeTableColumn,
  removeTableRow,
  replaceAt,
  replaceTableCell,
  tableColumnCount,
} from './editorModel';
import { ConditionBuilder } from './ConditionBuilder';
import { DocumentTextEditor } from './DocumentTextEditor';
import { paragraphsOfNode } from './richTextModel';
import { SelectorPicker } from './SelectorPicker';
import { reasonExplanation } from './migrationModel';
import { TechnicalDetails } from './TechnicalDetails';

/**
 * Das Formular eines einzelnen Elements (#1245).
 *
 * Je Art genau die Felder, die sie braucht. Eine DiNo-Variable wird über den
 * vorhandenen Selector-Picker gewählt, nicht als Pfad getippt; eine Frage bekommt
 * ihren Schlüssel vorgeschlagen und kann an ein Feld des Vorgangs gebunden werden.
 */

interface Props {
  node: IEditorNode;
  onChange: (node: IEditorNode) => void;
  questions: { questionId: string; prompt: string; answerType: string }[];
  blockTargets: { blockKey: string; title: string }[];
  disabled?: boolean;
  /** Wie tief im Baum — nur für die Einrückung; die Tiefe selbst ist nicht begrenzt. */
  depth?: number;
  /** Vorgang für die Wertvorschau in der Feldauswahl; ohne ihn bleibt sie datenfrei. */
  idLegalTransaction?: number | null;
}

const referenceFor = (
  blockKey: string,
  blockTargets: { blockKey: string; title: string }[]
): IBlockReference | null => {
  const target = blockTargets.find(item => item.blockKey === blockKey);
  return target
    ? {
        category: 'DiNo',
        area: '',
        blockName: target.title,
        resolvedBlockId: target.blockKey,
      }
    : null;
};

const BlockSelect: React.FC<{
  value: string;
  onChange: (_blockKey: string) => void;
  blockTargets: { blockKey: string; title: string }[];
  disabled?: boolean;
}> = ({ value, onChange, blockTargets, disabled }) => (
  <TextField
    size='small'
    select
    label='Baustein'
    value={value}
    onChange={event => onChange(event.target.value)}
    disabled={disabled}
    fullWidth
    helperText={
      blockTargets.length === 0
        ? 'Es gibt noch keinen anderen Abschnitt, auf den verwiesen werden könnte.'
        : undefined
    }
  >
    {blockTargets.map(target => (
      <MenuItem key={target.blockKey} value={target.blockKey}>
        {target.title}
      </MenuItem>
    ))}
    {value && !blockTargets.some(target => target.blockKey === value) && (
      // Nie ein leeres Feld zeigen, wenn ein Ziel gesetzt ist — sonst sieht es aus wie »kein
      // Baustein«, und wer etwas anderes wählt, verliert das Ziel unbemerkt.
      <MenuItem value={value} disabled>
        Baustein nicht in dieser Version gefunden
      </MenuItem>
    )}
  </TextField>
);

/**
 * Eine Tabelle bearbeiten.
 *
 * Modell, Laufzeit und Word tragen Tabellen seit dem Rich-Klon; der Editor kannte sie nicht. Das
 * war nicht nur eine fehlende Ansicht: `table` stand nicht unter den nativen Knotenarten, also
 * liess sich ein Abschnitt mit Tabelle **gar nicht speichern** und der Baum nicht veröffentlichen.
 *
 * Bewusst kein Word-Tabellendesigner. Eine Zelle ist ein Absatzstrom und bekommt deshalb
 * denselben Editor wie ein Textbaustein — mit Formatierung und DiNo-Feldern. Zeilen und Spalten
 * lassen sich anlegen und entfernen; Breiten, Rahmen und verbundene Zellen reisen unverändert
 * mit, auch wenn die Oberfläche sie nicht anbietet.
 */
const TableEditor: React.FC<{
  node: IEditorNode;
  onChange: (_node: IEditorNode) => void;
  disabled?: boolean;
}> = ({ node, onChange, disabled }) => {
  const rows = node.rows ?? [];
  const columns = tableColumnCount(node);
  const merged = hasMergedColumns(node);
  const mergedRows = hasMergedRows(node);

  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
        <Typography variant='caption' color='text.secondary'>
          {rows.length} Zeilen · {columns} Spalten
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size='small'
          startIcon={<Add />}
          disabled={disabled || mergedRows}
          onClick={() => onChange(addTableRow(node))}
        >
          Zeile
        </Button>
        <Button
          size='small'
          startIcon={<Add />}
          disabled={disabled || merged}
          onClick={() => onChange(addTableColumn(node))}
        >
          Spalte
        </Button>
      </Stack>

      {(merged || mergedRows) && (
        <Typography variant='caption' color='text.secondary'>
          Diese Tabelle hat verbundene Zellen. Ihr Inhalt lässt sich bearbeiten,
          ihr Aufbau nicht — welche Spalte eine verbundene Zelle meint, sagt die
          Vorlage nicht.
        </Typography>
      )}

      {rows.map((row, rowIndex) => (
        <Paper key={`row-${rowIndex}`} variant='outlined' sx={{ p: 1 }}>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ mb: 0.5 }}
          >
            <Typography variant='caption' color='text.secondary'>
              Zeile {rowIndex + 1}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title='Zeile entfernen'>
              <span>
                <IconButton
                  size='small'
                  disabled={disabled || mergedRows || rows.length <= 1}
                  onClick={() => onChange(removeTableRow(node, rowIndex))}
                  aria-label={`Zeile ${rowIndex + 1} entfernen`}
                >
                  <Delete fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Stack direction='row' spacing={1} alignItems='flex-start'>
            {(row.cells ?? []).map((cell, cellIndex) => (
              <Box key={`cell-${cellIndex}`} sx={{ flex: 1, minWidth: 140 }}>
                <Stack
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{ mb: 0.25 }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Spalte {cellIndex + 1}
                    {(cell.colSpan ?? 1) > 1 ? ` (über ${cell.colSpan})` : ''}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {rowIndex === 0 && (
                    <Tooltip title='Spalte entfernen'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={disabled || merged || columns <= 1}
                          onClick={() =>
                            onChange(removeTableColumn(node, cellIndex))
                          }
                          aria-label={`Spalte ${cellIndex + 1} entfernen`}
                        >
                          <Delete fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <DocumentTextEditor
                  paragraphs={paragraphsOfNode(cell)}
                  onChange={paragraphs =>
                    onChange(
                      replaceTableCell(node, rowIndex, cellIndex, {
                        ...cell,
                        paragraphs,
                      })
                    )
                  }
                  disabled={disabled}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

/**
 * Ein zusammengesetzter Zweig — Text, Variablen und weitere Bedingungen in ihrer Reihenfolge.
 *
 * Bis hierher wurde er nur angezeigt: „wird gezeigt, aber nicht umgebaut". Damit liess sich eine
 * importierte Verschachtelung ansehen, aber nie pflegen. Jetzt ist jedes Element für sich
 * bearbeitbar, und ein Element kann selbst wieder eine Bedingung sein — die Rekursion läuft über
 * denselben `NodeEditor`, damit ein Zweig nichts anderes kann als ein Abschnitt.
 *
 * Ein eigener Sonst-Zweig steht bewusst nicht darin: das Modell kennt keinen. Ein „sonst" ist
 * eine zweite Bedingung mit der Gegenbedingung — so entsteht es beim Import, und so bleibt es
 * hier. Eine zweite Darstellung desselben Sachverhalts wäre eine Falle.
 */
const BranchEditor: React.FC<{
  nodes: IEditorNode[];
  onChange: (_nodes: IEditorNode[]) => void;
  questions: Props['questions'];
  blockTargets: Props['blockTargets'];
  disabled?: boolean;
  depth: number;
  idLegalTransaction?: number | null;
}> = ({
  nodes,
  onChange,
  questions,
  blockTargets,
  disabled,
  depth,
  idLegalTransaction,
}) => {
  const [addAnchor, setAddAnchor] = React.useState<null | HTMLElement>(null);

  return (
    <Stack
      spacing={1}
      sx={{
        pl: 1.5,
        borderLeft: 2,
        // Je tiefer, desto kraeftiger die Linie: bei Tiefe 5 sieht man noch, wohin man gehoert.
        borderColor: depth % 2 === 0 ? 'divider' : 'primary.light',
      }}
    >
      {nodes.map((child, index) => (
        <Box
          key={`${child.type}-${index}`}
          sx={
            // Eine Karte nur fuer Struktur. Ein Textabsatz soll wie Text aussehen, nicht wie
            // ein Formular — sonst bestehen fuenf Ebenen aus zehn gleichen Kaesten. Eine
            // Bedingung mitten im Satz ist Text, kein Block (§ Inline-Bedingungen).
            CARD_NODE_KINDS.includes(child.type) && !isInlineCondition(child)
              ? {
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.25,
                  bgcolor: 'background.default',
                }
              : { pl: 0.5 }
          }
        >
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ mb: 0.5 }}
          >
            <Chip
              size='small'
              variant={
                CARD_NODE_KINDS.includes(child.type) &&
                !isInlineCondition(child)
                  ? 'filled'
                  : 'outlined'
              }
              label={
                child.type === 'conditional_text'
                  ? isInlineCondition(child)
                    ? 'Im Satz'
                    : 'Wenn'
                  : NODE_KIND_LABEL[child.type]
              }
            />
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title='Nach oben'>
              <span>
                <IconButton
                  size='small'
                  disabled={disabled || index === 0}
                  onClick={() => onChange(moveAt(nodes, index, -1))}
                  aria-label={`Element ${index + 1} nach oben`}
                >
                  <ArrowUpward fontSize='inherit' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Nach unten'>
              <span>
                <IconButton
                  size='small'
                  disabled={disabled || index === nodes.length - 1}
                  onClick={() => onChange(moveAt(nodes, index, 1))}
                  aria-label={`Element ${index + 1} nach unten`}
                >
                  <ArrowDownward fontSize='inherit' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Element entfernen'>
              <span>
                <IconButton
                  size='small'
                  disabled={disabled}
                  onClick={() => onChange(removeAt(nodes, index))}
                  aria-label={`Element ${index + 1} entfernen`}
                >
                  <Delete fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <NodeEditor
            node={child}
            onChange={next => onChange(replaceAt(nodes, index, next))}
            questions={questions}
            blockTargets={blockTargets}
            disabled={disabled}
            depth={depth + 1}
            idLegalTransaction={idLegalTransaction}
          />
        </Box>
      ))}

      {nodes.length === 0 && (
        <Typography variant='caption' color='text.secondary'>
          Dieser Zweig ist leer — er gibt nichts aus, wenn die Bedingung
          zutrifft.
        </Typography>
      )}

      <Box>
        <Button
          size='small'
          startIcon={<Add />}
          disabled={disabled}
          onClick={event => setAddAnchor(event.currentTarget)}
        >
          In den Zweig einfügen
        </Button>
        <Menu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          onClose={() => setAddAnchor(null)}
        >
          {BRANCH_NODE_KINDS.map(kind => (
            <MenuItem
              key={kind}
              onClick={() => {
                onChange([...nodes, newNode(kind)]);
                setAddAnchor(null);
              }}
            >
              {NODE_KIND_LABEL[kind]}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Stack>
  );
};

/** »Weiteres Element im Zweig …«: macht einen einfachen Zweig zu einem zusammengesetzten. */
const BranchGrow: React.FC<{
  disabled?: boolean;
  onGrow: (_kind: NodeKind) => void;
}> = ({ disabled, onGrow }) => {
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  return (
    <Box>
      <Button
        size='small'
        startIcon={<Add />}
        disabled={disabled}
        onClick={event => setAnchor(event.currentTarget)}
      >
        Weiteres Element im Zweig …
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {BRANCH_NODE_KINDS.map(kind => (
          <MenuItem
            key={kind}
            onClick={() => {
              onGrow(kind);
              setAnchor(null);
            }}
          >
            {NODE_KIND_LABEL[kind]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const NodeEditor: React.FC<Props> = ({
  node,
  onChange,
  questions,
  blockTargets,
  disabled,
  depth = 0,
  idLegalTransaction = null,
}) => {
  // Eine Bedingung mitten im Satz steht zusammengeklappt; aufgeklappt ist sie das gewohnte
  // Formular. Der Zustand gehoert zur Ansicht, nicht zum Dokument.
  const [expanded, setExpanded] = React.useState(false);
  // Derselbe Katalog wie in der Feldauswahl — react-query liefert ihn aus dem Cache. Er wird hier
  // nur gelesen, um Feldpfade zu benennen; ohne ihn bliebe der Pfad selbst stehen.
  const { selectorCatalog } = useArnoSelectorCatalog(idLegalTransaction);
  const fieldLabel = React.useMemo(
    () => fieldLabelResolver(selectorCatalog),
    [selectorCatalog]
  );
  const patch = (values: Partial<IEditorNode>) =>
    onChange({ ...node, ...values });

  if (node.type === 'table') {
    return <TableEditor node={node} onChange={onChange} disabled={disabled} />;
  }

  if (node.type === 'conditional_text' && node.nodes) {
    // Ein zusammengesetzter Zweig: die Bedingung oben, darunter sein Inhalt — jedes Element
    // für sich bearbeitbar, und ein Element darf selbst wieder eine Bedingung sein.
    return (
      <Stack spacing={1.5}>
        <ConditionBuilder
          condition={node.condition}
          onChange={condition => patch({ condition })}
          questions={questions}
          disabled={disabled}
          allowNone={false}
          idLegalTransaction={idLegalTransaction}
        />
        <Box>
          <Typography variant='caption' color='text.secondary'>
            Dann:
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <BranchEditor
              nodes={node.nodes}
              onChange={nodes => patch({ nodes })}
              questions={questions}
              blockTargets={blockTargets}
              disabled={disabled}
              depth={depth}
              idLegalTransaction={idLegalTransaction}
            />
          </Box>
        </Box>
      </Stack>
    );
  }

  if (
    node.type === 'conditional_text' &&
    isInlineCondition(node) &&
    !expanded
  ) {
    // Eine Stelle mitten im Satz: was sie ausgibt, und wann — in einer Zeile.
    const ausgabe = inlineConditionText(node);
    return (
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Typography
          variant='body2'
          sx={{ fontStyle: ausgabe ? 'normal' : 'italic' }}
        >
          {ausgabe || 'gibt nichts aus'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {conditionSummary(node.condition, questions, fieldLabel) ||
            'noch ohne Bedingung'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size='small'
          onClick={() => setExpanded(true)}
          disabled={disabled}
        >
          Bearbeiten
        </Button>
      </Stack>
    );
  }

  if (node.type === 'text' || node.type === 'conditional_text') {
    return (
      <Stack spacing={1.5}>
        {node.type === 'conditional_text' && isInlineCondition(node) && (
          <Stack direction='row' justifyContent='flex-end'>
            <Button size='small' onClick={() => setExpanded(false)}>
              Zuklappen
            </Button>
          </Stack>
        )}
        {node.type === 'conditional_text' && (
          <ConditionBuilder
            condition={node.condition}
            onChange={condition => patch({ condition })}
            questions={questions}
            disabled={disabled}
            allowNone={false}
            idLegalTransaction={idLegalTransaction}
          />
        )}
        {/* Gespeichert werden Absätze, nie HTML (`richDoc.ts`). */}
        <DocumentTextEditor
          paragraphs={paragraphsOfNode(node)}
          onChange={paragraphs => patch({ paragraphs, text: undefined })}
          disabled={disabled}
          questions={questions}
          idLegalTransaction={idLegalTransaction}
        />
        {node.type === 'conditional_text' && (
          <BranchGrow
            disabled={disabled}
            onGrow={kind =>
              // Aus den Absätzen wird der erste Textknoten des Zweigs, dahinter das neue Element.
              // Inhaltlich dasselbe — so wird aus einem einfachen Zweig ein verschachtelbarer
              // (#1245, Teil 43: vorher ließ sich eine verschachtelte Bedingung nur in einem Klon
              // pflegen, nie neu anlegen).
              onChange({
                ...node,
                paragraphs: undefined,
                text: undefined,
                nodes: [
                  { type: 'text', paragraphs: paragraphsOfNode(node) },
                  newNode(
                    kind,
                    questions.map(item => item.questionId)
                  ),
                ],
              })
            }
          />
        )}
      </Stack>
    );
  }

  if (node.type === 'info') {
    return (
      <TextField
        size='small'
        label='Hinweis an den Bearbeiter'
        value={node.text ?? ''}
        onChange={event => patch({ text: event.target.value })}
        disabled={disabled}
        multiline
        minRows={2}
        fullWidth
      />
    );
  }

  if (node.type === 'data_field') {
    return (
      <Stack spacing={1.5}>
        <SelectorPicker
          value={node.selector ?? ''}
          onChange={selector => patch({ selector })}
          disabled={disabled}
          idLegalTransaction={idLegalTransaction}
        />
        <Stack direction='row' spacing={1.5}>
          <TextField
            size='small'
            label='Beschriftung (optional)'
            value={node.label ?? ''}
            onChange={event => patch({ label: event.target.value || null })}
            disabled={disabled}
            fullWidth
          />
          <TextField
            size='small'
            label='Ersatztext, wenn der Vorgang nichts liefert'
            value={node.fallback ?? ''}
            onChange={event => patch({ fallback: event.target.value || null })}
            disabled={disabled}
            fullWidth
            helperText='Ohne Ersatz bleibt eine sichtbare Fehlstelle im Dokument.'
          />
        </Stack>
      </Stack>
    );
  }

  if (node.type === 'question') {
    return (
      <Stack spacing={1.5}>
        <TextField
          size='small'
          label='Frage'
          value={node.prompt ?? ''}
          onChange={event => patch({ prompt: event.target.value })}
          disabled={disabled}
          fullWidth
        />
        <TechnicalDetails
          entries={[
            { label: 'Kennung der Frage', value: node.questionId ?? '' },
          ]}
          label='ⓘ Technische Details'
        />
        <Stack
          direction='row'
          spacing={1.5}
          sx={{ flexWrap: 'wrap', gap: 1.5 }}
        >
          <TextField
            size='small'
            select
            label='Antwort'
            value={node.answerType ?? 'BOOLEAN'}
            onChange={event =>
              patch({ answerType: event.target.value as AnswerType })
            }
            disabled={disabled}
            sx={{ minWidth: 180 }}
          >
            {(Object.keys(ANSWER_TYPE_LABEL) as AnswerType[]).map(type => (
              <MenuItem key={type} value={type}>
                {ANSWER_TYPE_LABEL[type]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {node.answerType === 'SINGLE_CHOICE' && (
          <TextField
            size='small'
            label='Antwortmöglichkeiten (eine je Zeile)'
            value={(node.answerOptions ?? []).join('\n')}
            onChange={event =>
              patch({
                answerOptions: event.target.value
                  .split('\n')
                  .map(item => item.trim())
                  .filter(Boolean),
              })
            }
            disabled={disabled}
            multiline
            minRows={2}
            fullWidth
          />
        )}

        <TextField
          size='small'
          label='Hilfetext (optional)'
          value={node.helpText ?? ''}
          onChange={event => patch({ helpText: event.target.value || null })}
          disabled={disabled}
          fullWidth
        />

        <Box
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
        >
          <Typography variant='subtitle2'>
            Aus dem Vorgang beantworten
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Lässt sich die Frage aus den Daten des Vorgangs eindeutig
            beantworten, wird sie nicht gestellt.
          </Typography>
          <Stack
            direction='row'
            spacing={1.5}
            sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1.5 }}
          >
            <TextField
              size='small'
              select
              label='Datenquelle'
              value={node.binding ? 'yes' : 'no'}
              onChange={event =>
                patch({
                  binding:
                    event.target.value === 'yes'
                      ? {
                          selector: '',
                          interpretation: 'IS_NOT_EMPTY',
                          value: null,
                        }
                      : null,
                })
              }
              disabled={disabled}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value='no'>immer fragen</MenuItem>
              <MenuItem value='yes'>aus einem Feld</MenuItem>
            </TextField>

            {node.binding && (
              <>
                <Box sx={{ minWidth: 300, flexGrow: 1 }}>
                  <SelectorPicker
                    value={node.binding.selector}
                    onChange={selector =>
                      patch({ binding: { ...node.binding!, selector } })
                    }
                    disabled={disabled}
                    idLegalTransaction={idLegalTransaction}
                  />
                </Box>
                <TextField
                  size='small'
                  select
                  label='Auswertung'
                  value={node.binding.interpretation}
                  onChange={event =>
                    patch({
                      binding: {
                        ...node.binding!,
                        interpretation: event.target
                          .value as typeof node.binding.interpretation,
                      },
                    })
                  }
                  disabled={disabled}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value='IS_NOT_EMPTY'>Ja, wenn gefüllt</MenuItem>
                  <MenuItem value='IS_EMPTY'>Ja, wenn leer</MenuItem>
                  <MenuItem value='EQUALS'>Ja, wenn gleich …</MenuItem>
                  <MenuItem value='DIRECT'>Wert übernehmen</MenuItem>
                </TextField>
                {node.binding.interpretation === 'EQUALS' && (
                  <TextField
                    size='small'
                    label='Vergleichswert'
                    value={node.binding.value ?? ''}
                    onChange={event =>
                      patch({
                        binding: {
                          ...node.binding!,
                          value: event.target.value,
                        },
                      })
                    }
                    disabled={disabled}
                    sx={{ minWidth: 180 }}
                  />
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    );
  }

  if (node.type === 'choice') {
    const options = node.options ?? [];
    const setOptions = (next: IChoiceOption[]) => patch({ options: next });
    return (
      <Stack spacing={1.5}>
        <TextField
          size='small'
          label='Frage'
          value={node.prompt ?? ''}
          onChange={event => patch({ prompt: event.target.value })}
          disabled={disabled}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(node.multiple)}
              onChange={event => patch({ multiple: event.target.checked })}
              disabled={disabled}
            />
          }
          label='Mehrere Antworten möglich'
        />
        {options.map((option, index) => (
          <Stack
            key={index}
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
          >
            <Stack spacing={1} sx={{ flexGrow: 1 }}>
              <TextField
                size='small'
                label='Antwort'
                value={option.label}
                onChange={event =>
                  setOptions(
                    replaceAt(options, index, {
                      ...option,
                      label: event.target.value,
                    })
                  )
                }
                disabled={disabled}
              />
              <TextField
                size='small'
                label='Text, der dann erscheint'
                value={option.text ?? ''}
                onChange={event =>
                  setOptions(
                    replaceAt(options, index, {
                      ...option,
                      text: event.target.value,
                    })
                  )
                }
                disabled={disabled}
                multiline
                minRows={2}
              />
              <BlockSelect
                value={option.reference?.resolvedBlockId ?? ''}
                onChange={blockKey =>
                  setOptions(
                    replaceAt(options, index, {
                      ...option,
                      reference: referenceFor(blockKey, blockTargets),
                    })
                  )
                }
                blockTargets={blockTargets}
                disabled={disabled}
              />
            </Stack>
            <Tooltip title='Antwort entfernen'>
              <span>
                <IconButton
                  size='small'
                  color='error'
                  disabled={disabled || options.length <= 2}
                  onClick={() => setOptions(removeAt(options, index))}
                >
                  <DeleteOutline fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
        <Box>
          <Button
            size='small'
            startIcon={<Add />}
            disabled={disabled}
            onClick={() =>
              setOptions([
                ...options,
                { slot: String(options.length + 1), label: '', text: '' },
              ])
            }
          >
            Antwort hinzufügen
          </Button>
        </Box>
      </Stack>
    );
  }

  if (node.type === 'input') {
    return (
      <TextField
        size='small'
        label='Beschriftung der Eingabe'
        value={node.prompt ?? ''}
        onChange={event => patch({ prompt: event.target.value })}
        disabled={disabled}
        fullWidth
      />
    );
  }

  if (
    node.type === 'block_reference' ||
    node.type === 'conditional_block_reference'
  ) {
    return (
      <Stack spacing={1.5}>
        {node.type === 'conditional_block_reference' && (
          <ConditionBuilder
            condition={node.condition}
            onChange={condition => patch({ condition })}
            questions={questions}
            disabled={disabled}
            allowNone={false}
            idLegalTransaction={idLegalTransaction}
          />
        )}
        <BlockSelect
          value={node.reference?.resolvedBlockId ?? ''}
          onChange={blockKey =>
            patch({ reference: referenceFor(blockKey, blockTargets) })
          }
          blockTargets={blockTargets}
          disabled={disabled}
        />
      </Stack>
    );
  }

  if (node.type === 'migration_required') {
    // Der Grund zuerst im Klartext, der Code nur in den technischen Details: »UNSUPPORTED_
    // WORD_FIELD« sagt einer Notariatsmitarbeiterin nichts, »Diese alte Word-Bedingung konnte
    // nicht sicher übernommen werden« schon.
    return (
      <Stack spacing={1}>
        <Typography variant='body2'>
          {reasonExplanation(node.reasonCode ?? '')}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Bitte durch ein passendes Element ersetzen und diese Stelle dann
          löschen — solange sie hier steht, lässt sich der Baum nicht
          veröffentlichen.
        </Typography>
        <TechnicalDetails
          entries={[
            { label: 'Grund', value: node.reason ?? '' },
            { label: 'Kennung', value: node.reasonCode ?? '' },
          ]}
        />
      </Stack>
    );
  }

  return (
    <Typography variant='body2' color='text.secondary'>
      Dieses Element hat keine Einstellungen.
    </Typography>
  );
};
